'use client';

import { c as _c } from "react/compiler-runtime";
import { useAddToWatchlist, useRemoveFromWatchlist } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { HowItWorksInfoButton } from '@/components/how-it-works/how-it-works-info-button';
import { WhatIsAProposalModal } from '@/components/how-it-works/what-is-a-proposal-modal';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ProposalShareButton } from '@/components/launchpad/proposal-share-button';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { AnimatedBookmark } from '@/components/ui/animated-bookmark';
import { resolveProposalCategory } from '@/config/proposal-display';
import { TOAST_IDS } from '@/config/toast-ids';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { trackEvent } from '@/lib/analytics';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
interface ProposalHeaderProps {
  proposal: {
    pubkey: string;
    title: string;
    description: string | null;
    coverImageUrl: string | null;
    /**
     * NSFW flag for `coverImageUrl` (spec 071). When true the cover is
     * rendered through the NSFW-blur overlay until the viewer taps to
     * reveal. Optional — defaults to `false` at the callsite.
     */
    coverImageNsfw?: boolean;
    category: string;
    /** Influencer / Featured origin mark on the cover's corner. */
    marketType?: MarketIdentityData['marketType'];
  };
  className?: string;
}
const FALLBACK_EMOJI = '\u{1F4A1}'; // 💡

/**
 * Proposal detail page header — Figma 3317:1388 top half. An 88px square
 * thumbnail (88px, `rounded-control` / 12px corners — cover image when present, otherwise a category-emoji fallback),
 * a top-right action row (Bookmark · Share), then the title and the
 * full description below. The Panel wrapper, status banner, and right-sidebar
 * identity card are owned by `proposal-detail-page.tsx`.
 */
export function ProposalHeader(t0) {
  const $ = _c(62);
  const {
    proposal,
    className
  } = t0;
  const t = useTranslations("launchpad.detail");
  const tWatch = useTranslations("common.watchlist");
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kind: "proposals",
      fields: ["pubkeys"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: watchlist
  } = useAuthenticatedWatchlist(t1);
  const {
    ensureAuthenticated
  } = useAuthGate();
  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();
  let t2;
  if ($[1] !== proposal.pubkey || $[2] !== watchlist?.pubkeys) {
    t2 = watchlist?.pubkeys?.includes(proposal.pubkey) ?? false;
    $[1] = proposal.pubkey;
    $[2] = watchlist?.pubkeys;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isSaved = t2;
  let t3;
  if ($[4] !== addToWatchlist || $[5] !== ensureAuthenticated || $[6] !== isSaved || $[7] !== proposal.pubkey || $[8] !== removeFromWatchlist || $[9] !== tWatch) {
    t3 = function handleBookmarkToggle() {
      if (!ensureAuthenticated()) {
        return;
      }
      if (isSaved) {
        removeFromWatchlist.mutate({
          kind: "proposals",
          pubkey: proposal.pubkey
        });
      } else {
        addToWatchlist.mutate({
          kind: "proposals",
          pubkey: proposal.pubkey
        });
      }
      showToast({
        id: isSaved ? TOAST_IDS.WATCHLIST_REMOVED : TOAST_IDS.WATCHLIST_SAVED,
        message: isSaved ? tWatch("removed") : tWatch("added"),
        variant: "success"
      });
      trackEvent("proposal_bookmark_toggled", {
        proposalPubkey: proposal.pubkey,
        saved: !isSaved
      });
    };
    $[4] = addToWatchlist;
    $[5] = ensureAuthenticated;
    $[6] = isSaved;
    $[7] = proposal.pubkey;
    $[8] = removeFromWatchlist;
    $[9] = tWatch;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleBookmarkToggle = t3;
  const hasCover = proposal.coverImageUrl !== null && proposal.coverImageUrl.length > 0;
  const fallbackEmoji = resolveProposalCategory(proposal.category)?.emoji ?? FALLBACK_EMOJI;
  const description = proposal.description ?? "";
  let t4;
  if ($[11] !== className) {
    t4 = cn("flex flex-col gap-6", className);
    $[11] = className;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== fallbackEmoji || $[14] !== hasCover || $[15] !== proposal.category || $[16] !== proposal.coverImageNsfw || $[17] !== proposal.coverImageUrl || $[18] !== proposal.pubkey || $[19] !== proposal.title) {
    t5 = hasCover ? <OptimizedImage variant="thumbnail" entity={{
      kind: "market",
      pubkey: proposal.pubkey,
      category: String(proposal.category ?? "").toLowerCase(),
      coverImageUrl: proposal.coverImageUrl,
      nsfw: proposal.coverImageNsfw ?? false
    }} alt={proposal.title} size="size-22" rounded="rounded-control" priority={true} /> : <div aria-hidden="true" className="flex size-22 shrink-0 items-center justify-center rounded-control bg-background-tertiary text-5xl leading-none"><span>{fallbackEmoji}</span></div>;
    $[13] = fallbackEmoji;
    $[14] = hasCover;
    $[15] = proposal.category;
    $[16] = proposal.coverImageNsfw;
    $[17] = proposal.coverImageUrl;
    $[18] = proposal.pubkey;
    $[19] = proposal.title;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== proposal.marketType || $[22] !== t5) {
    t6 = <ThumbWithOrigin marketType={proposal.marketType}>{t5}</ThumbWithOrigin>;
    $[21] = proposal.marketType;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== proposal.title) {
    t7 = <h1 className="min-w-0 flex-1 text-heading text-foreground-primary @min-[1024px]/proposal-detail:hidden">{proposal.title}</h1>;
    $[24] = proposal.title;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setHowItWorksOpen(true);
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t) {
    t9 = t("howProposalsWorkLabel");
    $[27] = t;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <HowItWorksInfoButton onClick={t8} ariaLabel={t9} className="bg-background-tertiary" />;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t) {
    t11 = t("bookmarkLabel");
    $[31] = t;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== handleBookmarkToggle || $[34] !== isSaved || $[35] !== t11) {
    t12 = <AnimatedBookmark isSaved={isSaved} onClick={handleBookmarkToggle} ariaLabel={t11} className="bg-background-tertiary" />;
    $[33] = handleBookmarkToggle;
    $[34] = isSaved;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== proposal.pubkey || $[38] !== proposal.title) {
    t13 = <ProposalShareButton proposalPubkey={proposal.pubkey} proposalTitle={proposal.title} />;
    $[37] = proposal.pubkey;
    $[38] = proposal.title;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t10 || $[41] !== t12 || $[42] !== t13) {
    t14 = <div className="flex w-full shrink-0 items-center justify-end gap-2 @min-[768px]/proposal-detail:w-auto">{t10}{t12}{t13}</div>;
    $[40] = t10;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t14 || $[45] !== t6 || $[46] !== t7) {
    t15 = <div className="flex flex-wrap items-center justify-between gap-3 @min-[768px]/proposal-detail:flex-nowrap @min-[768px]/proposal-detail:items-start">{t6}{t7}{t14}</div>;
    $[44] = t14;
    $[45] = t6;
    $[46] = t7;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== proposal.title) {
    t16 = <h1 className="hidden text-heading text-foreground-primary @min-[1024px]/proposal-detail:block">{proposal.title}</h1>;
    $[48] = proposal.title;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== description) {
    t17 = description.length > 0 ? <p className="text-body text-foreground-tertiary whitespace-pre-wrap break-words">{description}</p> : null;
    $[50] = description;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t16 || $[53] !== t17) {
    t18 = <div className="flex min-w-0 flex-col gap-1">{t16}{t17}</div>;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== howItWorksOpen) {
    t19 = <WhatIsAProposalModal open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />;
    $[55] = howItWorksOpen;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t15 || $[58] !== t18 || $[59] !== t19 || $[60] !== t4) {
    t20 = <div className={t4}>{t15}{t18}{t19}</div>;
    $[57] = t15;
    $[58] = t18;
    $[59] = t19;
    $[60] = t4;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  return t20;
}